"use client";

import React from "react";
import { motion } from "framer-motion";
import AnimatedBlogCard, { BlogPost } from "@/components/ui/animated-blog-card";
import { siteConfig } from "@/lib/config";
import { Meteors } from "@/components/ui/meteors";

// For demo purposes - in a real app, this would come from a CMS or API
const blogPosts: BlogPost[] = [
  {
    id: "1",
    title: "Foundation Models: Transforming AI Development",
    summary: "An exploration of how foundation models like GPT-4 and LLaMA are revolutionizing AI development and accelerating enterprise adoption across industries.",
    category: "AI/ML",
    tags: ["Foundation Models", "GPT", "Large Language Models", "Enterprise AI"],
    author: "Dr. Emily Zhang",
    authorImage: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTJ8fHBvcnRyYWl0fGVufDB8fDB8fHww",
    published: "Mar 15, 2025",
    image: "https://images.unsplash.com/photo-1677442135066-f56478fc4c08?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTJ8fGFpJTIwbW9kZWx8ZW58MHx8MHx8fDA%3D",
    slug: "foundation-models-transforming-ai-development",
  },
  {
    id: "2",
    title: "Neural Networks Explained: From Perceptrons to Transformers",
    summary: "A comprehensive, accessible guide to understanding neural network architectures and how they've evolved from simple perceptrons to sophisticated transformer models.",
    category: "AI/ML",
    tags: ["Neural Networks", "Deep Learning", "Transformers", "AI Education"],
    author: "Dr. James Wilson",
    authorImage: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTh8fHBvcnRyYWl0fGVufDB8fDB8fHww",
    published: "Mar 5, 2025",
    image: "https://images.unsplash.com/photo-1679673177212-8a011a4f86f7?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OHx8bmV1cmFsJTIwbmV0d29ya3xlbnwwfHwwfHx8MA%3D",
    slug: "neural-networks-explained-perceptrons-transformers",
  },
  {
    id: "3",
    title: "Responsible AI: Building Ethical Machine Learning Systems",
    summary: "Examining the critical ethical considerations when developing and deploying AI systems, with practical guidelines for building responsible AI in enterprise environments.",
    category: "AI/ML",
    tags: ["Ethics", "Responsible AI", "Governance", "Bias Mitigation"],
    author: "Dr. Sarah Mitchell",
    authorImage: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NHx8cG9ydHJhaXR8ZW58MHx8MHx8fDA%3D",
    published: "Feb 20, 2025",
    image: "https://images.unsplash.com/photo-1633613286848-e6f43bbafb8d?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MXx8ZXRoaWNhbCUyMGFpfGVufDB8fDB8fHww",
    slug: "responsible-ai-building-ethical-machine-learning-systems",
  },
  {
    id: "4",
    title: "From Data to Decisions: The Machine Learning Pipeline",
    summary: "A detailed walkthrough of the end-to-end machine learning pipeline, from data collection and preparation to model deployment and monitoring in production environments.",
    category: "AI/ML",
    tags: ["Machine Learning", "Data Pipeline", "MLOps", "Production ML"],
    author: "Michael Thompson",
    authorImage: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MzJ8fHBvcnRyYWl0fGVufDB8fDB8fHww",
    published: "Feb 12, 2025",
    image: "https://images.unsplash.com/photo-1599658880436-c61792e70672?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8ZGF0YSUyMHBpcGVsaW5lfGVufDB8fDB8fHww",
    slug: "data-to-decisions-machine-learning-pipeline",
  },
  {
    id: "5",
    title: "Computer Vision in Practice: Case Studies and Applications",
    summary: "Exploring real-world applications of computer vision technology across industries, from manufacturing quality control to medical diagnostics and autonomous vehicles.",
    category: "AI/ML",
    tags: ["Computer Vision", "Image Recognition", "Object Detection", "Industry Applications"],
    author: "David Nguyen",
    authorImage: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mjh8fHBvcnRyYWl0fGVufDB8fDB8fHww",
    published: "Jan 28, 2025",
    image: "https://images.unsplash.com/photo-1507146153580-69a1fe6d8aa1?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8Y29tcHV0ZXIlMjB2aXNpb258ZW58MHx8MHx8fDA%3D",
    slug: "computer-vision-practice-case-studies-applications",
  },
  {
    id: "6",
    title: "Natural Language Processing: Understanding Human Language",
    summary: "A deep dive into how NLP technologies are enabling machines to understand, interpret, and generate human language, with insights into recent breakthroughs and practical applications.",
    category: "AI/ML",
    tags: ["NLP", "Language Models", "Text Analysis", "Conversational AI"],
    author: "Jessica Lee",
    authorImage: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NDB8fHBvcnRyYWl0fGVufDB8fDB8fHww",
    published: "Jan 10, 2025",
    image: "https://images.unsplash.com/photo-1555952494-efd681c7e3f9?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTB8fGxhbmd1YWdlfGVufDB8fDB8fHww",
    slug: "natural-language-processing-understanding-human-language",
  },
];

export default function BlogPage() {
  return (
    <div className="relative w-full overflow-hidden min-h-screen">
      {/* Animated background effect */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <Meteors quantity={20} />
      </div>
      
      <div className="relative z-10 pt-24 pb-16">
        <div className="container mx-auto px-4 text-center mb-16">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6"
          >
            {siteConfig.blog.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto"
          >
            {siteConfig.blog.subtitle}
          </motion.p>
        </div>

        <AnimatedBlogCard posts={blogPosts} />
      </div>
    </div>
  );
}
